import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { APPLY_URL } from '../mock';

export const CoachSection3D = ({ onApplyClick }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const imageRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -20;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };

  return (
    <section ref={ref} className="relative bg-panel py-24 md:py-32 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16 space-y-6"
          initial={{ opacity: 0, y: 100 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            className="font-accent text-xl md:text-2xl italic text-gold"
            initial={{ opacity: 0, scale: 0 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            The Coach · Why CT3 Exists
          </motion.p>
          <motion.h2 
            className="font-hero text-3xl md:text-5xl lg:text-6xl uppercase tracking-wide text-cream leading-tight"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            style={{ fontWeight: 700 }}
          >
            Built by Someone Who
            <br />
            <span className="text-gold">Understands How</span>
            <br />
            You Operate.
          </motion.h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -200, rotateY: -45 }}
            animate={inView ? { opacity: 1, x: 0, rotateY: 0 } : {}}
            transition={{ delay: 0.4, duration: 1, type: "spring", stiffness: 60 }}
            style={{ perspective: '2000px' }}
          >
            <motion.div
              ref={imageRef}
              className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              animate={{
                rotateX: mousePosition.y * 0.3,
                rotateY: mousePosition.x * 0.3,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_driven-12week/artifacts/qoggg5dq_B25544AF-B9CD-4614-9A0B-DDAD38ACC571.png"
                alt="Clifton Tant - Founder & Head Coach"
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-transparent to-transparent opacity-60" />
              
              <div 
                className="absolute inset-4 border-2 border-gold/40 group-hover:border-gold transition-colors duration-500"
                style={{ transform: 'translateZ(30px)' }}
              />
              
              <motion.div
                className="absolute bottom-8 left-8 bg-dark/80 backdrop-blur-sm border border-gold/50 px-6 py-4"
                style={{ transform: 'translateZ(60px)' }}
                whileHover={{ scale: 1.05 }}
              >
                <p className="font-display text-2xl uppercase tracking-wide text-gold mb-1">
                  Clifton Tant
                </p>
                <p className="font-body text-sm text-gold-soft">
                  Founder & Head Coach
                </p>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 200 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.5, duration: 1, type: "spring", stiffness: 60 }}
          >
            <p className="font-body text-base md:text-lg text-[#e8e6df] leading-relaxed font-light">
              I'm <span className="font-display text-gold uppercase tracking-wide">Clifton Tant</span>. I built CT3 because the standard fitness model fails high-performing professionals — not because they lack discipline, but because generic programs ignore how they actually operate.
            </p>
            <p className="font-body text-base md:text-lg text-[#e8e6df] leading-relaxed font-light">
              Most programs are built for someone with unlimited time, low stress, and no competing priorities. <span className="italic text-gold-soft">That's not you.</span>
            </p>
            <p className="font-body text-base md:text-lg text-[#e8e6df] leading-relaxed font-light">
              CT3 is a structured system that connects your training, nutrition, and daily habits into one framework that compounds week over week — built specifically around your schedule, your recovery capacity, and your goal.
            </p>
            <p className="font-body text-base md:text-lg text-[#e8e6df] leading-relaxed font-light">
              Every client gets a program engineered for their life. <span className="font-accent italic text-gold">Not a template. Not a course.</span> Direct coaching with weekly adjustments based on real data.
            </p>

            <div className="pt-6">
              <motion.div
                whileHover={{ scale: 1.05, rotateX: 5 }}
                whileTap={{ scale: 0.95 }}
                style={{ perspective: '1000px' }}
                className="inline-block"
              >
                <Button
                  onClick={() => window.open(APPLY_URL, '_blank', 'noopener,noreferrer')}
                  className="bg-[#c6a04a] hover:bg-[#d9be7e] text-[#0d0d0d] font-semibold text-sm md:text-base px-6 md:px-8 py-5 md:py-6 rounded-none uppercase tracking-wider transition-all duration-300 shadow-2xl shadow-[#c6a04a]/30 whitespace-normal h-auto"
                >
                  Apply for Your Strategy Call
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
